<template>
  <teleport v-if="pipContainer" :to="pipContainer">
    <twilio-pip-dialer @keypad:toggle="resizePipWindow" />
  </teleport>

  <!-- Call notes modal for incoming calls accepted via PiP -->
  <teleport v-if="callDialog && showCallDialog" to="#modals">
    <communication-log-call-dialog
      class="fixed z-50 shadow-lg"
      :value="callDialog"
      @close-dialog="closeCallDialog"
    />
  </teleport>
</template>

<script setup lang="ts">
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  shallowRef,
  toRaw,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";

import * as Sentry from "@sentry/vue";
import { Call, Device } from "@twilio/voice-sdk";
import { storeToRefs } from "pinia";

import { AnalyticsEvent } from "@/enums/analytics";
import {
  TwilioCallActions,
  TwilioCallDirection,
  TwilioCallErrors,
  TwilioCallEvents,
} from "@/enums/communicationProviders";
import { TWILIO_PIP_OPEN } from "@/helpers/constants/events";
import {
  leaveChannel,
  subscribeToChannelEvents,
} from "@/helpers/websockets/channels";
import { CALL_CHANNEL } from "@/helpers/websockets/common";
import { useEmitter } from "@/hooks/common";
import { useTwilio } from "@/hooks/communicationProviders";
import { useNotification } from "@/hooks/notifications";
import { useAnalyticsStore } from "@/stores/analytics";
import { CALL_REQUEST_TYPES, useCallsStore } from "@/stores/calls";

import CommunicationLogCallDialog from "@/components/communicationLogs/communication/CommunicationLogCallDialog.vue";
import TwilioPipDialer from "@/components/twilio/TwilioPiPDialer.vue";

import type { ActiveCallSession } from "@/stores/calls";

const deviceOptions = {
  closeProtection: true,
  codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
  tokenRefreshMs: 30_000,
  edge: ["umatilla", "ashburn", "dublin", "singapore", "sao-paulo"],
  maxCallSignalingTimeoutMs: 30_000,
} satisfies Device.Options;

const deviceSize = {
  width: 340,
  height: 450,
  keypadWidth: 180,
} as const;

const emitter = useEmitter();
const { t } = useI18n();
const callStore = useCallsStore();
const {
  activeCall,
  activeCallSession,
  callDialog,
  callRequestData,
  isDialing,
  callDirection,
  isMicrophoneMuted,
  isRingtoneSilenced,
  device,
  hasIncomingCall,
  incomingCall,
  isPiPOpen,
  showCallTimer,
} = storeToRefs(callStore);
const { showMessage } = useNotification();
const { twilioAccessToken, fetchTwilioToken } = useTwilio();
const { trackEvent } = useAnalyticsStore();

const pipContainer = shallowRef<HTMLElement | null>(null);
const pipWindow = shallowRef<Window | null>(null);

const showCallDialog = ref(false);
const subscribedCallId = ref<string | null>(null);

const isSupported = computed(() => "documentPictureInPicture" in window);

const copyStyles = (pip: Window) => {
  document.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => {
    pip.document.head.appendChild(node.cloneNode(true));
  });
};

const openPiP = async (options?: {
  width?: number;
  height?: number;
}): Promise<Window | null> => {
  if (!isSupported.value) {
    showMessage(t("ERRORS.TWILIO.PIP_NOT_SUPPORTED"), "error");
    return null;
  }
  if (pipWindow.value && !pipWindow.value.closed) {
    pipWindow.value.close();
  }
  try {
    // @ts-expect-error - documentPictureInPicture is not in TypeScript types yet
    const pip = await window.documentPictureInPicture.requestWindow({
      width: options?.width ?? deviceSize.width,
      height: options?.height ?? deviceSize.height,
    });
    pipWindow.value = pip;
    copyStyles(pip);
    return pip;
  } catch {
    trackEvent(AnalyticsEvent.TwilioError, {
      description: TwilioCallErrors.PiPError,
      explanation: t("ERRORS.TWILIO.FAILED_TO_OPEN_PIP"),
    });
    showMessage(t("ERRORS.TWILIO.FAILED_TO_OPEN_PIP"), "error");
    return null;
  }
};

const resizePipWindow = (isKeypadVisible: boolean) => {
  if (!pipWindow.value || pipWindow.value.closed) {
    return;
  }

  const targetInnerWidth = isKeypadVisible
    ? pipWindow.value.outerWidth + deviceSize.keypadWidth
    : pipWindow.value.outerWidth - deviceSize.keypadWidth;

  // Keep the same inner height by preserving outer height
  const targetOuterWidth = Math.round(targetInnerWidth);
  const targetOuterHeight = Math.round(pipWindow.value.outerHeight);

  pipWindow.value.resizeTo(targetOuterWidth, targetOuterHeight);
};

const closeCallDialog = () => {
  showCallDialog.value = false;
  callDialog.value = null;
};

const handlePiPClose = () => {
  callStore.togglePipState(false);
  pipWindow.value = null;
  pipContainer.value = null;
  trackEvent(AnalyticsEvent.TwilioPipClosed);
};

const handlePiPOpen = async () => {
  if (!isSupported.value) {
    showMessage(t("ERRORS.TWILIO.PIP_NOT_SUPPORTED"), "error");
    return;
  }

  const existingPip = window.documentPictureInPicture?.window;
  if (existingPip && !existingPip.closed) {
    existingPip.focus();
    return;
  }

  if (callStore.isCallActive && !activeCall.value && !incomingCall.value) {
    showMessage(t("ERRORS.TWILIO.CALL_ACTIVE_OTHER_TAB"), "warning");
    return;
  }

  const pip = await openPiP({
    width: deviceSize.width,
    height: deviceSize.height,
  });
  if (!pip) {
    showMessage(t("ERRORS.TWILIO.PIP_NOT_OPEN"), "error");
    return;
  }
  // Create a container div in the PiP window
  const container = pip.document.createElement("div");
  container.id = "twilio-pip-root";
  container.style.height = "100%";
  pip.document.body.appendChild(container);
  pip.document.body.style.margin = "0";
  pip.document.body.style.padding = "0";
  pip.document.body.style.height = "100%";
  pip.document.documentElement.style.height = "100%";

  pipContainer.value = container;
  callStore.togglePipState(true);
  trackEvent(AnalyticsEvent.TwilioPipOpened);

  // Set up Device when PiP opens - only PiP tab has the device
  await setupDevice();

  // Listen for PiP window close to properly clean up state
  pip.addEventListener("pagehide", handlePiPClose);

  // If there's an incoming call, notify other tabs to stop their sound
  if (hasIncomingCall.value) {
    callStore.notifyIncomingCallHandled();
  }

  // Only initiate a NEW outgoing call — skip if a call is already in progress
  // (e.g. PiP was closed mid-call and reopened)
  if (
    activeCallSession.value &&
    device.value &&
    !callStore.isCallActive &&
    !isDialing.value
  ) {
    initiateOutgoingCall(activeCallSession.value);
  }
};

const setupDevice = async () => {
  if (device.value) {
    return;
  }
  if (!twilioAccessToken.value) {
    await fetchTwilioToken();
  }
  if (!twilioAccessToken.value) {
    return;
  }
  try {
    const device = new Device(twilioAccessToken.value, deviceOptions);
    await device.register();
    device.audio?.incoming(false);
    device.on(TwilioCallEvents.Incoming, handleIncomingCall);
    device.on(TwilioCallEvents.TokenWillExpire, handleTokenExpiring);
    device.on(TwilioCallEvents.Error, handleError);
    callStore.setDevice(device);
    trackEvent(AnalyticsEvent.TwilioDeviceRegistered);
  } catch (error) {
    trackEvent(AnalyticsEvent.TwilioError, {
      description: TwilioCallErrors.DeviceError,
      explanation: t("ERRORS.TWILIO.DEVICE_SET_UP_ERROR"),
    });
    showMessage(t("ERRORS.TWILIO.DEVICE_SET_UP_ERROR"), "error");
    Sentry.captureException(error);
  }
};

const handleIncomingCall = (call: Call) => {
  trackEvent(AnalyticsEvent.TwilioIncomingCall);
  callStore.setIncomingCall(call);

  if (pipContainer.value) {
    callStore.notifyIncomingCallHandled();
    call.on(TwilioCallActions.Cancel, handleCallEnded);
    call.on(TwilioCallActions.Disconnect, handleCallEnded);
  }
};

const handleTokenExpiring = async () => {
  await fetchTwilioToken();
  if (twilioAccessToken.value && device.value) {
    toRaw(device.value)?.updateToken(twilioAccessToken.value);
    trackEvent(AnalyticsEvent.TwilioTokenRefreshed);
  }
};

const handleError = (error: { code: number; message: string }) => {
  showMessage(t("ERRORS.TWILIO.DEVICE_ERROR"), "error");
  Sentry.captureException(error);
};

const cleanupDevice = () => {
  pipWindow.value = null;
  pipContainer.value = null;
  const currentDevice = toRaw(device.value);
  if (!currentDevice) {
    return;
  }
  currentDevice.unregister();
  currentDevice.removeAllListeners();
  callStore.setDevice(null);
};

const handleCallEnded = () => {
  trackEvent(AnalyticsEvent.TwilioCallEnded, {
    direction: callDirection.value,
  });
  // Unsubscribe from call channel when call ends
  if (subscribedCallId.value) {
    leaveChannel(CALL_CHANNEL, subscribedCallId.value);
    subscribedCallId.value = null;
  }
  callStore.clearCallState();
};

const initiateOutgoingCall = async (session?: ActiveCallSession | null) => {
  if (!session?.data || !device.value) {
    showMessage(t("ERRORS.TWILIO.FAILED_TO_INITIATE_CALL"), "error");
    return;
  }
  isDialing.value = true;
  callDirection.value = TwilioCallDirection.Outgoing;
  activeCallSession.value = session;
  try {
    const call = await toRaw(device.value).connect({
      params: {
        To: session.to,
        sessionId: session.data.session_id ?? "",
      },
    });
    callStore.setActiveCall(call);
    isDialing.value = false;
    showCallTimer.value = true;
    trackEvent(AnalyticsEvent.TwilioOutgoingCall);

    // Subscribe to call channel to receive phone number updates
    if (session.data.id) {
      subscribedCallId.value = session.data.id.toString();
      subscribeToChannelEvents(CALL_CHANNEL, subscribedCallId.value);
    }

    call.on(TwilioCallActions.Disconnect, handleCallEnded);
    call.on(TwilioCallActions.Cancel, handleCallEnded);
    call.on(TwilioCallActions.Mute, (isMuted: boolean) => {
      isMicrophoneMuted.value = isMuted;
    });
  } catch (error) {
    trackEvent(AnalyticsEvent.TwilioError, {
      description: TwilioCallErrors.ConnectionError,
      explanation: t("ERRORS.TWILIO.FAILED_TO_INITIATE_CALL"),
    });
    showMessage(t("ERRORS.TWILIO.FAILED_TO_INITIATE_CALL"), "error");
    callStore.clearCallState();
  }
};

// Watch for outgoing call requests from this tab
// Only the PiP tab (with device) can initiate calls
watch(activeCallSession, (session) => {
  if (!session) {
    return;
  }
  // Only initiate if this tab has PiP open and has the device
  if (!pipContainer.value || !device.value) {
    return;
  }
  initiateOutgoingCall(session);
});

// Watch for call requests from OTHER tabs via BroadcastChannel
watch(
  callRequestData,
  (payload) => {
    if (!payload) {
      return;
    }
    // Another tab is handling the incoming call - stop our sound
    if (payload.type === CALL_REQUEST_TYPES.INCOMING_CALL_HANDLED) {
      callStore.stopIncomingSound();
      return;
    }
    // Another tab silenced/muted the call - stop our sound
    if (payload.type === CALL_REQUEST_TYPES.CALL_SILENCED) {
      callStore.stopIncomingSound();
      isRingtoneSilenced.value = true;
      return;
    }
    if (payload.type === CALL_REQUEST_TYPES.CALL_ENDED) {
      try {
        if (activeCall.value) {
          toRaw(activeCall.value).disconnect();
        }

        if (incomingCall.value) {
          toRaw(incomingCall.value).reject();
        }
      } catch { /* already ended */ }
      callStore.clearCallState();
      return;
    }
    // Phone number update from another tab - sync to our stores
    if (
      payload.type === CALL_REQUEST_TYPES.PHONE_NUMBER_UPDATE &&
      payload.phoneNumber
    ) {
      if (activeCallSession.value) {
        activeCallSession.value = {
          ...activeCallSession.value,
          data: {
            ...activeCallSession.value.data,
            from: payload.phoneNumber,
          } as typeof activeCallSession.value.data,
        };
      }
      return;
    }
    // Only handle outgoing call broadcasts if PiP is open in THIS tab
    if (!pipContainer.value) {
      return;
    }
    if (payload.type === CALL_REQUEST_TYPES.INITIATE_CALL && payload.session) {
      initiateOutgoingCall(payload.session);
    }
  },
  { immediate: false }
);

watch(callDialog, (newValue) => {
  if (newValue) {
    showCallDialog.value = true;
  }
});

// Update device token when it changes (e.g., manual refresh via RefreshVoiceToken)
watch(twilioAccessToken, (newToken) => {
  if (newToken && device.value) {
    toRaw(device.value)?.updateToken(newToken);
  }
});

onMounted(async () => {
  callStore.setPiPSupported(isSupported.value);
  emitter.on(TWILIO_PIP_OPEN, handlePiPOpen);
  if (!isPiPOpen.value) {
    await setupDevice();
  }
});

onUnmounted(() => {
  if (pipWindow.value) {
    pipWindow.value.close();
  }
  emitter.off(TWILIO_PIP_OPEN, handlePiPOpen);
  cleanupDevice();
});
</script>
